/**
 * صفحة منتج جديد واحد — /new-product/<slug>
 * بلوكاتها من `new_product_blocks` مربوطةً بصفّ الصفحة في `new_product_pages`،
 * وتُدار كلّها من /admin/new-product.
 */
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { DICTS } from "@/lib/i18n";
import { SiteShell } from "@/components/SiteShell";
import { NewProductBlocks } from "@/components/newproduct/NewProductBlocks";
import { getNewProductPages } from "@/utils/newProductBlocks.functions";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";

import { publicCacheHeaders } from "@/lib/public-cache-headers";

export const Route = createFileRoute("/new-product/$slug")({
  headers: publicCacheHeaders,
  loader: async ({ params }) => {
    const { pages } = await getNewProductPages();
    const page = pages.find((p) => p.slug === params.slug);
    if (!page) throw notFound();
    return { page };
  },
  head: ({ loaderData, params }) => {
    const name = loaderData?.page?.nameEn || loaderData?.page?.nameAr || "";
    const title = name
      ? `${name} — Lecmo`
      : DICTS.en["newproduct.meta.title"];
    const url = `${PUBLIC_SITE_ORIGIN}/new-product/${params.slug}`;
    const cover = loaderData?.page?.coverUrl || "";
    return {
      meta: [
        { title },
        { name: "description", content: DICTS.en["newproduct.meta.description"] },
        { property: "og:title", content: title },
        {
          property: "og:description",
          content: DICTS.en["newproduct.meta.description"],
        },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(loaderData ? [] : [{ name: "robots", content: "noindex" }]),
        ...(cover && !/\.(mp4|webm|mov|m4v)(\?|$)/i.test(cover)
          ? [
              { property: "og:image", content: cover },
              { name: "twitter:image", content: cover },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: NewProductSlugPage,
  errorComponent: NewProductSlugError,
  notFoundComponent: NewProductSlugNotFound,
});

function NewProductSlugPage() {
  const { slug } = Route.useParams();
  return (
    <SiteShell>
      <NewProductBlocks slug={slug} />
    </SiteShell>
  );
}

function NewProductSlugNotFound() {
  return (
    <SiteShell>
      <div className="mx-auto flex min-h-[60svh] max-w-[720px] flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="font-serif text-2xl">404</p>
        <Link to="/new-product" className="font-sans text-sm underline">
          /new-product
        </Link>
      </div>
    </SiteShell>
  );
}

function NewProductSlugError() {
  return (
    <SiteShell>
      <div className="mx-auto flex min-h-[60svh] max-w-[720px] items-center justify-center px-6">
        <p className="font-sans text-sm text-muted-foreground">—</p>
      </div>
    </SiteShell>
  );
}
